import { RADIUS_VALUES, TRACKING_VALUES, type WebsiteDefinition } from "@baze/schema";
import { bestOnColor, contrastRatio, mixHex } from "./color";
import type { CompiledCatalog, Design, DesignTokens, Font } from "./types";

export interface ResolvedTheme {
  design: Design;
  tokens: DesignTokens;
  fonts: { heading: Font; body: Font };
  /** Warnings when user overrides reduce contrast (shown in the editor, never blocking render). */
  warnings: string[];
}

type Overrides = NonNullable<WebsiteDefinition["theme"]["overrides"]>;

/** Corner presets the editor offers, as the design's four radius tokens. */
export const RADIUS: Record<NonNullable<Overrides["radius"]>, DesignTokens["radius"]> = RADIUS_VALUES;

const TRACKING: Record<NonNullable<Overrides["headingTracking"]>, string> = TRACKING_VALUES;

/**
 * A design's tokens with the owner's overrides applied (colours, corners, headings, texture), plus
 * contrast warnings for the editor. Also used by the web runtime on tokens resolved by the API.
 */
export function applyThemeOverrides(base: DesignTokens, overrides: Overrides | undefined): { tokens: DesignTokens; warnings: string[] } {
  const o = overrides ?? {};
  const warnings: string[] = [];
  const colors = { ...base.colors };
  if (o.background || o.text) {
    const bg = o.background ?? colors.background;
    const text = o.text ?? (o.background ? bestOnColor(bg, colors.text, colors.onInverted) : colors.text);
    colors.background = bg;
    colors.text = text;
    colors.surface = mixHex(bg, text, 0.045);
    colors.border = mixHex(bg, text, 0.14);
    colors.muted = mixHex(text, bg, 0.32);
    if (contrastRatio(text, bg) < 4.5) warnings.push("Teks kurang kontras dengan warna latar.");
  }
  if (o.primary) {
    colors.primary = o.primary;
    colors.onPrimary = bestOnColor(o.primary);
    if (contrastRatio(colors.onPrimary, o.primary) < 4.5) warnings.push("Teks di atas tombol kurang kontras dengan warna utama.");
  }
  if ((o.primary || o.background) && contrastRatio(colors.primary, colors.background) < 3) warnings.push("Warna utama kurang kontras dengan latar halaman.");
  if (o.accent) {
    colors.accent = o.accent;
    colors.secondary = mixHex(o.accent, colors.background, 0.55);
  }
  const tokens: DesignTokens = { ...base, colors, heading: { ...base.heading } };
  if (o.radius) tokens.radius = RADIUS[o.radius];
  if (o.headingCase) tokens.heading.case = o.headingCase;
  if (o.headingWeight) tokens.heading.weight = o.headingWeight;
  if (o.headingTracking) tokens.heading.tracking = TRACKING[o.headingTracking];
  if (o.texture) tokens.texture = o.texture;
  if (o.motion) tokens.motion = o.motion;
  return { tokens, warnings };
}

/**
 * Resolve the theme for a definition: design by id (+ version fallback to latest) with user overrides applied.
 * Returns null if the design does not exist in the catalog (callers render a safe fallback design).
 */
export function resolveTheme(catalog: Pick<CompiledCatalog, "designs" | "fonts" | "fontPairs">, theme: WebsiteDefinition["theme"]): ResolvedTheme | null {
  const candidates = catalog.designs.filter((d) => d.id === theme.id);
  const design = candidates.find((d) => d.version === theme.version) ?? candidates.sort((a, b) => b.version - a.version)[0];
  if (!design) return null;
  const { tokens, warnings } = applyThemeOverrides(design.tokens, theme.overrides);
  const pairId = theme.overrides?.fontPair ?? design.fontPair;
  const pair = catalog.fontPairs.find((p) => p.id === pairId) ?? catalog.fontPairs.find((p) => p.id === design.fontPair);
  const font = (id: string | undefined) => catalog.fonts.find((f) => f.id === id) ?? catalog.fonts[0]!;
  return { design, tokens, fonts: { heading: font(pair?.heading), body: font(pair?.body) }, warnings };
}

/**
 * Fonts and pairs named by section and text styles (section.style.theme.fontPair, style.texts[].font):
 * what the engine needs in `theme.library`, and the extra font files a live page must load.
 */
export function styleFontLibrary(catalog: Pick<CompiledCatalog, "fonts" | "fontPairs">, definition: { pages?: WebsiteDefinition["pages"] }): { fonts: Font[]; pairs: CompiledCatalog["fontPairs"] } {
  const fontIds = new Set<string>();
  const pairIds = new Set<string>();
  for (const page of definition.pages ?? []) {
    for (const s of page.sections) {
      if (s.style?.theme?.fontPair) pairIds.add(s.style.theme.fontPair);
      for (const t of Object.values(s.style?.texts ?? {})) if (t.font) fontIds.add(t.font);
    }
  }
  const pairs = catalog.fontPairs.filter((p) => pairIds.has(p.id));
  for (const p of pairs) fontIds.add(p.heading).add(p.body);
  return { fonts: catalog.fonts.filter((f) => fontIds.has(f.id)), pairs };
}
